/**
 * `Rial` — every monetary value in this system. AGENTS.md §7 prohibition 4 and
 * §8.
 *
 * An integer number of Rial. Not a float, not a string, not a `number` that
 * happens to be whole: a branded integer that a bare `number` cannot be passed
 * to without going through `rial()`.
 *
 * ## Why the type carries the weight
 *
 * `0.1 + 0.2 === 0.30000000000000004`. In a ledger that is not a curiosity, it
 * is a trial balance that does not balance by a few billionths and an
 * accountant who cannot find the difference. Storing minor units as integers
 * removes the representation error rather than rounding it away afterwards.
 *
 * Rial has no minor unit in practice — there is no sub-Rial coin — so the
 * integer *is* the amount. That makes this simpler than a cents-based design:
 * there is no scaling factor to get wrong.
 *
 * ## Rial and Toman
 *
 * Prices are commonly quoted in Toman, which is ten Rial. AGENTS.md §8 is
 * explicit that this is **a display setting applied in the UI only** — the
 * stored value, every calculation, and every API payload are Rial. A Toman
 * value never exists below the render boundary, for the same reason a Jalali
 * date never does: two units in circulation is two chances to add them
 * together.
 */
export class InvalidRial extends Error {
    constructor(value, reason) {
        super(`invalid Rial amount ${JSON.stringify(value)}: ${reason}`);
        this.name = 'InvalidRial';
    }
}
/**
 * The largest amount that can be represented exactly.
 *
 * `Number.MAX_SAFE_INTEGER` is about 9.007 × 10^15 Rial — roughly 900 trillion
 * Toman. Beyond it, integer arithmetic silently stops being exact, which is the
 * one failure this type exists to prevent, so the boundary is checked rather
 * than assumed to be out of reach. If a tenant ever legitimately approaches it,
 * that is a signal to move to `bigint` and a `numeric` column, not to raise the
 * limit.
 */
export const MAX_RIAL = Number.MAX_SAFE_INTEGER;
export const MIN_RIAL = -Number.MAX_SAFE_INTEGER;
export function rial(value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new InvalidRial(value, 'not a finite number');
    }
    if (!Number.isInteger(value)) {
        // The most likely cause is a float that arrived from a calculation done
        // outside this package — which is the prohibition, not an accident of
        // formatting.
        throw new InvalidRial(value, 'Rial is a whole number. A fractional amount here means the arithmetic that produced it ' +
            'was done in floating point somewhere outside @sakhtar/money (AGENTS.md §7 prohibition 4)');
    }
    if (value > MAX_RIAL || value < MIN_RIAL) {
        throw new InvalidRial(value, `outside the exactly representable range ±${MAX_RIAL}`);
    }
    // -0 and 0 are the same amount, and only one of them should ever be stored.
    return (value === 0 ? 0 : value);
}
export function isRial(value) {
    return typeof value === 'number' && Number.isInteger(value) && Math.abs(value) <= MAX_RIAL;
}
export const ZERO = rial(0);
/**
 * Parses the wire form. ADR-005 decision 5: money in a payload is serialized as
 * a digit string, because a JSON number is a float and AGENTS.md §7 prohibition
 * 4 does not stop at the database boundary.
 */
export function rialFromString(text) {
    if (!/^-?\d+$/.test(text)) {
        throw new InvalidRial(text, 'expected an optionally signed run of digits. A decimal point, a thousands separator or an ' +
            'exponent all mean the value passed through a float on its way here');
    }
    const value = Number(text);
    if (!Number.isSafeInteger(value)) {
        throw new InvalidRial(text, `outside the exactly representable range ±${MAX_RIAL}`);
    }
    return rial(value);
}
/** The wire form. Always a string, never a JSON number. */
export const rialToString = (amount) => String(amount);
export const amountOf = (amount) => amount;
//# sourceMappingURL=rial.js.map